import { useEffect, useState } from 'react';
import { clientPortalApi, type RouteMetricsResult, type SyntaxMetricSlimKey, type SyntaxRange } from '../api';
import { buildSyntaxValueByEdge } from './syntaxZones';

export const CONNECTIVITY_METRICS: Array<{ key: SyntaxMetricSlimKey; label: string }> = [
  { key: 'integrationGlobal', label: 'Integración global' },
  { key: 'integrationR800', label: 'Integración local (800 m)' },
  { key: 'structuralPriority', label: 'Prioridad estructural' },
  { key: 'choiceGlobal', label: 'Flujo de paso' },
];

/**
 * Conectividad necesita ambas direcciones a la vez (mismo patrón
 * "overview" que Velocidades/Infraestructura), más el valor de sintaxis
 * de la métrica seleccionada por edge y el rango de red completo (para
 * el modo "Automático" — a diferencia de velocidad, que auto-escala por
 * recorrido, sintaxis espacial es una propiedad de RED: la misma calle
 * debe verse del mismo color sin importar qué línea la incluya).
 * `route-metrics` solo se pide para obtener la geometría por-tramo
 * (`pavement.segments`), no para su contenido de pavimento.
 */
export function useConnectivityData(
  slug: string,
  token: string,
  analysisId: string,
  isCircuit: boolean,
  metric: SyntaxMetricSlimKey
): {
  loadState: 'loading' | 'error' | 'ready';
  ida: RouteMetricsResult | null;
  vuelta: RouteMetricsResult | null;
  valueByEdge: Map<string, number | null>;
  range: SyntaxRange | null;
  hasMetrics: boolean;
} {
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [ida, setIda] = useState<RouteMetricsResult | null>(null);
  const [vuelta, setVuelta] = useState<RouteMetricsResult | null>(null);
  const [valueByEdge, setValueByEdge] = useState<Map<string, number | null>>(new Map());
  const [range, setRange] = useState<SyntaxRange | null>(null);
  const [hasMetrics, setHasMetrics] = useState(false);

  useEffect(() => {
    if (!token || !analysisId) return;
    let cancelled = false;
    setLoadState('loading');

    const idaPromise = clientPortalApi.getRouteMetrics(slug, analysisId, token, isCircuit ? 'CIRCUITO' : 'IDA');
    const vueltaPromise = isCircuit ? null : clientPortalApi.getRouteMetrics(slug, analysisId, token, 'VUELTA');
    const syntaxPromise = clientPortalApi.getSyntaxMetrics(slug, analysisId, token, metric);
    const rangePromise = clientPortalApi.getSyntaxRange(slug, analysisId, token);

    Promise.all([idaPromise, vueltaPromise ?? Promise.resolve(null), syntaxPromise, rangePromise])
      .then(([idaResult, vueltaResult, syntaxSlim, syntaxRange]) => {
        if (cancelled) return;
        setIda(idaResult);
        setVuelta(vueltaResult);
        setValueByEdge(buildSyntaxValueByEdge(syntaxSlim.edges));
        setRange(syntaxRange);
        setHasMetrics(syntaxSlim.hasMetrics);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId, isCircuit, metric]);

  return { loadState, ida, vuelta, valueByEdge, range, hasMetrics };
}
